<template lang="pug">
.__page-map
  .search(
    @click="isSearch = !isSearch"
  )
    .search__magnifier(v-if="!isSearch")
    .search__close(v-else)
  .filter(
    :class="{ filter_active: isSearch }"
    v-click-outside="hideSmartSearch"
  )
    slot(name="filter")
  yandex-map.map(
    v-if="isActive && points"
    :cluster-options="$options.clusterOptions"
    :controls="[]"
    :detailed-controls="$options.controls"
    :scroll-zoom="true"
    :coords="mapCenter"
    :zoom="mapZoom"
    :use-object-manager="true"
    :init-without-markers="false"
    @map-was-initialized="onMapInitialized"
  )
    ymap-marker(
      cluster-name="eventPoints"
      marker-type="placemark"
      v-for="point in points"
      :key="point.uniqId"
      :balloon-template="balloonLayout"
      :coords="point.coords"
      :hintContent="point.title"
      :icon="$options.icon"
      :marker-id="point.id"
    )
  spinner.spinner(v-if="showSpinner")
  .notification(v-else) {{ emptyText }}
</template>

<script>
import spinner from '@/components/ext/Spinner_new.vue';
import { yandexMap, ymapMarker } from 'vue-yandex-maps';
import { balloonLayout, updateCluster, updateMarker } from './mapUtils';

const DEFAULT_COORDS = [55.753215, 37.622504];
const DEFAULT_ZOOM = 4;
const CLUSTER_NAME = 'eventPoints';
const MAX_ZOOM = 18;
let delay;

export default {
  components: {
    yandexMap,
    ymapMarker,
    spinner,
  },

  props: {
    emptyText: {
      type: String,
      default:
        'К сожалению выбранным параметрам не удовлетворяет ни одна программа.',
    },

    center: {
      type: Array,
      default: () => [],
    },

    zoom: {
      type: Number,
      default: null,
    },

    updateEvents: {
      type: Function,
    },

    filtered: {
      type: [Number, String],
      default: null,
    },

    points: {
      type: Array,
      required: true,
    },

    contain: {
      type: Boolean,
      default: false,
    },

    routeName: {
      type: String,
      default: 'event-card',
    },
  },
  mounted() {
    // Чтобы компонент карт активировался только в браузере
    this.$nextTick(() => {
      this.isActive = true;
    });
  },

  data: () => ({
    placemarks: [],
    isActive: false,
    isSearch: false,
    mapInstance: null,
    geoObjects: [],
    currentZoom: DEFAULT_ZOOM,
    cashedCoords: null,
    balloonLayout,
  }),

  computed: {
    mapZoom() {
      const { zoom, currentZoom } = this;
      return zoom || currentZoom;
    },

    mapCenter() {
      const { cashedCoords, points } = this;
      return (
        cashedCoords ||
        (points && points[0] && points[0].coords) ||
        DEFAULT_COORDS
      );
    },

    isPointsDefined() {
      const { points } = this;
      return points && points.length > 0;
    },

    showSpinner() {
      return true;
    },
  },

  watch: {
    // Можем следить за points, поскольку при изменении массива переинициализуруется весь компонент карты
    points(newValue, oldValue) {
      if (this.geoObjects.length) {
        // TODO FIXIT - убрать на уровне запроса updateEvents
        // когда появится возможность получать инфу пачкой.
        // убираем множественные обращения
        if (delay) clearTimeout(delay);
        delay = setTimeout(this.redrawBalloon, 80);
      }
    },
  },

  mounted() {
    // Чтобы компонент карт активировался только в браузере
    this.$nextTick(() => {
      this.isActive = true;
    });
  },

  methods: {
    onMapInitialized(mapInstance) {
      this.mapInstance = mapInstance;
      this.mapInstance.options.set('maxZoom', MAX_ZOOM);
      this.mapInstance.events.add('balloonclose', e => {
        this.geoObjects = [];
      });
      this.mapInstance.geoObjects.events.add('balloonopen', e => {
        let balloon = e.get('target');
        let data = balloon.getData();
        if (!this.updateEvents) return;
        if (data.type === 'Cluster') {
          updateCluster(
            data,
            balloon,
            this.updateEvents,
            this.$router,
            this.routeName,
          );
        } else if (data.type === 'Feature' && !data.properties.__UPDATED__) {
          updateMarker(
            data,
            balloon,
            this.updateEvents,
            this.$router,
            this.routeName,
          );
        }
      });

      // Если zoom и центр не определены, то показываем все переданные объекты
      if (!this.zoom && !this.center.length) {
        const bounds = mapInstance.geoObjects.getBounds();
        if (bounds) {
          mapInstance
            .setBounds(bounds, {
              checkZoomRange: true,
              useMapMargin: true,
              zoomMargin: [0, 0, 0, 0],
            })
            .then(() => {
              if (mapInstance.getZoom() > MAX_ZOOM) {
                mapInstance.setZoom(MAX_ZOOM);
              }
              this.currentZoom = mapInstance.getZoom();
              this.cashedCoords = mapInstance.getCenter();
            })
            .catch(err => console.error(err));
        }
      }
    },

    hideSmartSearch(e) {
      if (
        e.target.className ===
          'ymaps-2-1-71-events-pane ymaps-2-1-71-user-selection-none' &&
        this.isSearch == true
      )
        this.isSearch = false;
    },
  },
  // Настройки иконки и кластера лучше вынести в опции, поскольку для них реактивность не нужна
  icon: {
    layout: 'default#image',
    // Следующую строчку можно сделать через requre('./путь-к-картинке.svg') и не вставлять сюда base64,
    // т. к. require сам сделает из картинки base64
    imageHref: require('./svg/marker-map.svg'),
    imageSize: [30, 42],
    imageOffset: [-15, -42],
  },

  clusterOptions: {
    [CLUSTER_NAME]: {
      clusterIconColor: '#6e6bce',
      // Используем макет "карусель"
      clusterBalloonContentLayout: 'cluster#balloonCarouselContent',
      // Запрещаем зацикливание списка при постраничной навигации
      clusterBalloonCycling: true,
      // Настройка внешнего вида панели навигации. Элементами панели навигации будут маркеры.
      clusterBalloonPagerType: 'numeric',
      // Количество элементов в панели навигации
      clusterBalloonPagerSize: 6,
      numbers: [2, 4],
      layout: balloonLayout,
      balloonPagerVisible: false,
      clusterBalloonContentLayoutWidth: 300,
      clusterBalloonContentLayoutHeight: 340,
    },
  },

  controls: {
    geolocationControl: { options: { float: 'right' } },
    zoomControl: { options: { position: { right: 10, top: 50 } } },
  },
};
</script>

<style lang="stylus" scoped>
@import './style/mixin'
@import '~@/stylus/helpers/mediaqueriesStep'

.__page-map
  @extends $relative
  min-height 600px

  .search
    @extends $bgWhite
    size(28px)
    absolute(58px,10px)
    border-radius 4px
    box-shadow 0 1px 2px 1px rgba(0, 0, 0, .15), 0 2px 5px -3px rgba(0, 0, 0,.15)
    cursor pointer
    z-index 1

    &__magnifier,
    &__close
      @extends $relative
      size(28px)

      &::before,
      &::after
        @extends $content
        display block

    &__magnifier
      &::before
        size(7px)
        absolute(6px, 7px)
        border-radius 100%
        background-color transparent
        border solid 3px $search

      &::after
        @extends $searchIconBg
        size(6px, 4px)
        absolute(18px, 15px)
        transform rotate(59deg)

    &__close
      &::before,
      &::after
        @extends $searchIconBg
        size(15px, 3px)
        absolute(13px, 7px)

      &::after
        transform rotate(45deg)

      &::before
        transform rotate(-45deg)

  .filter
    size(255px, 490px)
    absolute(10px, -100%)
    transition left .8s ease-in
    z-index 1

    &_active
      absolute(10px, 45px)
      left 15%
      transition left .8s ease-out

      +tablet()
        left 6%

      +desktop()
        left 3.5%

  .map
    size(100%, 600px)

  .notification
    @extends $textCenter
    absolute(50%, 300px)
    width calc(100% - 300px)
</style>

<style lang="stylus">
@import './style/mixin'

.__page-map
  .spinner
    absolute(calc(50% - 50px))
    width 100px
    z-index -1

  .l-place__balloon
    @extends $bgWhite
    absolute(0)
    display block
    z-index 1010


  ymaps[class*="-balloon__content"]
    margin 0 !important
    padding 0 !important

  ymaps[class*="-balloon-content__header"]
    @extends $noneImportant

  ymaps[class*="-cluster-content__header"]
    @extends $noneImportant

  ymaps[class*="-balloon__close"]
    @extends $bgWhite

  ymaps[class*="-balloon__close-button"]
    size(30px)

  ymaps[class="ymaps-2-1-76-b-cluster-carousel__pager"]
    width 300px

  .place__balloon-inner
    box-shadow 0 0 3px 1px rgba($black, .2)
    min-height 350px

  .place__balloon-content
    size(300px, 340px, true)
    padding 10px
    display flex
    flex-direction column
    justify-content space-between

  a.place__balloon-close
    @extends $bgWhite
    absolute(0, ,0)
    font(20px)
    size(20px)
    text-decoration none
    color #7c7c7c
    z-index 20

    &:hover
      @extends $colorBlack

  .place__balloon-image
    background-color #f3f3f3
    margin-bottom 10px
    min-height 140px
    background-size cover
    background-repeat no-repeat
    background-position 50% 50%

  .place__balloon-title
    @extends $hidden, $colorBlack, $fontWeight
    font(16px)
    text-transform uppercase
    margin-bottom 4px
    max-height 54px

  .place__balloon-address
    @extends $hidden, $colorBlack
    font()
    flex-grow 1

  .place__balloon-metro
    font()
    margin-bottom 10px

    &:before
      @extends $relative, $inlineBlock, $content
      top -2px
      width 15px
      border-top 5px solid
      margin-right 6px
    span
      @extends $colorBlack

  .l-place__balloon-button
    @extends $textCenter

  .place__balloon-button
    @extends $bgWhite, $inlineBlock, $fontWeight
    font(12px, 2.7em)
    padding 0 2.5em
    height 3em
    border-radius .2em
    vertical-align middle
    transition all .3s ease
    text-decoration none
    cursor pointer
    border-width 2px
    border-style solid
    color $main

    &:hover
      background-color $main
      border-color $main
      color $white

    .text
      line-height 100%

    &:disabled
      opacity .7

      &:hover
        cursor default

    &:active
      box-shadow inset .5em .3em 1em rgba($black, .1)

  .place__balloon-button
    text-transform uppercase

  .place__balloon-carousel
    absolute(0)
    background-color #ededed
    box-shadow 0 0 3px 1px rgba($black, .2)
    padding 10px
</style>



// WEBPACK FOOTER //
// Map.vue?4714e25b